import * as React from 'react';
import {useRoute, useRouter} from 'react-router5';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {TabComponent, TabProps} from 'components/Tab';
import {transition} from 'helpers/css';

export interface TabbarProps {
  param?: string;
  children: TabComponent | TabComponent[];
}

const Container = styled.div`
  display: grid;
  gap: 20px;
`;

const Header = styled.header`
  position: relative;
  
  gap: 24px;
  grid-auto-flow: column;
  justify-content: flex-start;
  display: inline-grid;
  width: 100%;
`;

const HeaderContainer = styled.div`
  position: relative;
  display: grid;
  
  &:before {
    content: "";
    
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    
    background: ${({theme}) => theme.variables.borderColor};
    height: 2px;
  }
`;

const ControlWrapper = styled.label<Pick<TabProps, 'disabled'>>`
  cursor: ${({disabled}) => !disabled && 'pointer'};
  display: inline-grid;
`;

const ControlTitle = styled.span`
  position: relative;
  padding: 0 4px;
  margin-bottom: 24px;
  border-radius: 30px;
  
  color: ${({theme}) => transparentize(.7, theme.variables.color)};
  font-size: 15px;
  font-weight: 500;
  user-select: none;
  white-space: nowrap;
  
  ${transition()};
  
  &:before {
    content: "";
    
    position: absolute;
    right: 0;
    bottom: calc(-24px - 1px);
    left: 0;
    
    height: 4px;
    border-radius: 30px;
    
    ${transition()};
  }
`;

const ControlInput = styled.input`
  position: absolute;
  opacity: 0;
  
  &:checked + {
    ${ControlTitle} {
      color: ${({theme}) => theme.variables.color};
      
      &:before {
        background: ${({theme}) => theme.colors.primary};
      }
    }
  }
  
  &:disabled + {
    ${ControlTitle} {
      opacity: .3;
    }
  }
  
  &:hover:not(:checked):not(:disabled) + {
    ${ControlTitle} {
      color: ${({theme}) => transparentize(.4, theme.variables.color)};
      
      &:before {
        opacity: 1;
        background: ${({theme}) => transparentize(.8, theme.variables.color)};
      }
    }
  }
  
  &:focus-visible:not(:disabled) + {
    ${ControlTitle} {
      background: ${({theme}) => `
        ${transparentize(.95, theme.variables.color)}
      `};
      box-shadow: 0 0 0 5px ${({theme}) => `
        ${transparentize(.95, theme.variables.color)}
      `};
    }
  }
  
  &:active:not(:checked):not(:disabled) + {
    ${ControlTitle} {
      color: ${({theme}) => transparentize(.2, theme.variables.color)};
      
      &:before {
        opacity: 1;
        background: ${({theme}) => transparentize(.6, theme.variables.color)};
      }
    }
  }
`;

/**
 * This component groups tabs and renders a specific one
 * @param {TabbarProps} props
 * @constructor
 */
export default function Tabbar(
    props: TabbarProps,
): React.ReactElement<TabbarProps> {
  const {route} = useRoute();
  const {navigate} = useRouter();
  const paramName = props.param || 'tab';

  /**
   * The name of current tab
   * @return {string}
   */
  function getCurrentName() {
    return route.params[paramName] || (
      Array.isArray(props.children) ? (
        props.children[0].props.name
      ) : (
        props.children.props.name
      )
    );
  }

  /**
   * Get the current tab component
   * @return {TabComponent | undefined}
   */
  function getCurrentTab(): TabComponent | undefined {
    return Array.isArray(props.children) ? (
      props.children.find(({props}) => (
        props.name === getCurrentName()
      ))
    ) : props.children;
  }

  /**
   * Render the header with controls
   * @constructor
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    /**
     * Render controls of children tabs
     * @return {JSX.Element | JSX.Element[]}
     */
    function renderControls(): JSX.Element | JSX.Element[] {
      /**
       * Render the control
       * @param {TabComponent} control
       * @param {number} index
       * @return {JSX.Element}
       */
      function renderControl(
          control: TabComponent,
          index: number,
      ): JSX.Element {
        /**
         * Set current of changed control
         * @param {TabProps} tabName
         * @return {void}
         */
        function handleChange(tabName: TabProps['name']): void {
          navigate(route.name, {
            ...route.params,
            [paramName]: tabName,
          });
        }

        return (
          <ControlWrapper
            key={control.props.name}
            disabled={control.props.disabled}
          >
            <ControlInput
              type="radio"
              name={`tabbar-${index}`}
              checked={getCurrentName() === control.props.name}
              disabled={control.props.disabled}
              onChange={() => handleChange(control.props.name)}
            />
            <ControlTitle>
              {control.props.title}
            </ControlTitle>
          </ControlWrapper>
        );
      }
      return Array.isArray(props.children) ? (
        props.children.map((tab, index) => (
          renderControl(tab, index)
        ))) : (renderControl(props.children, 999));
    }

    return (
      <HeaderContainer>
        <Header>
          {renderControls()}
        </Header>
      </HeaderContainer>
    );
  }
  return (
    <Container>
      {renderHeader()}
      <div>
        {getCurrentTab()}
      </div>
    </Container>
  );
}
